import {
  Button,
  Grid,
  Header,
  SegmentedControl,
  Select,
  SpaceBetween,
  Table,
  TextFilter,
} from '@cloudscape-design/components';
import { PartTypes } from '@parallel-fluidics/shared/constants';
import PPartDisplay from 'components/Projects/pPartDisplay';
import LotDisplay from 'features/lots/components/LotDisplay';
import LotKindBadge from 'features/lots/components/LotKindBadge';
import LotStatusBadge from 'features/lots/components/LotStatusBadge';
import useLotsQuery from 'features/lots/hooks/useLotsQuery';
import getPPartOption from 'features/pPart/utils/getPPartOption';
import { useGetProjectParts } from 'hooks/use-basicQueries';
import useDebouncedValue from 'hooks/useDebouncedValue';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { kindOptions, statusOptions } from './lotFilterOptions';

const ALL_PARTS = { label: 'All parts', value: null };

/**
 * The paginated bags list with its filter row: code prefix, project →
 * part cascading selects, kind/status segmented controls. All filter
 * state lives in the URL (via onFiltersChange) so the view is shareable
 * and other pages deep-link it.
 */
export default function BagsTab({
  projectDocId = null,
  pPartDocId = null,
  kind = 'all',
  status = 'all',
  filterText = '',
  onFiltersChange,
  onClearFilters,
  projectFilterOptions,
  selectedProjectOption,
  projectsLoading,
}) {
  const navigate = useNavigate();
  const debouncedFilterText = useDebouncedValue(filterText);

  const { data: parts, isLoading: partsLoading } = useGetProjectParts({
    projectDocId,
    // Lots are bags of consumables — Tool parts can't have lots. No
    // status filter: old bags legitimately point at retired parts.
    excludeTypes: [PartTypes.TOOL, PartTypes.PISTON],
    disabled: !projectDocId,
  });

  const partFilterOptions = useMemo(
    () => [ALL_PARTS, ...(parts || []).map((part) => getPPartOption(part))],
    [parts],
  );
  const selectedPartOption = pPartDocId
    ? (partFilterOptions.find((option) => option.value === pPartDocId)
      || { label: pPartDocId, value: pPartDocId })
    : ALL_PARTS;

  const hasActiveFilters = Boolean(
    projectDocId || pPartDocId || kind !== 'all' || status !== 'all' || filterText,
  );

  const {
    CustomPagination,
    isFetchingNextPage,
    pageData,
  } = useLotsQuery({
    filterText: debouncedFilterText,
    kind,
    status,
    projectDocId,
    pPartDocId,
  });

  const columnDefinitions = useMemo(
    () => [
      {
        id: 'code',
        header: 'Code',
        // eslint-disable-next-line react/no-unstable-nested-components
        cell: (item) => (
          <Button variant="inline-link" onClick={() => navigate(`/lots/${item.id}`)}>
            {item.code}
          </Button>
        ),
      },
      {
        id: 'item',
        header: 'Item',
        // eslint-disable-next-line react/no-unstable-nested-components
        cell: (item) => (
          <PPartDisplay
            fieldName="fullId"
            projectDocId={item.projectDocId}
            pPartDocId={item.pPartDocId}
            fallbackMessage="—"
          />
        ),
      },
      {
        id: 'kind',
        header: 'Kind',
        // eslint-disable-next-line react/no-unstable-nested-components
        cell: (item) => <LotKindBadge kind={item.kind} />,
      },
      {
        id: 'qty',
        header: 'Qty (remaining / initial)',
        cell: (item) => `${item.remainingQty} / ${item.initialQty}`,
      },
      {
        id: 'status',
        header: 'Status',
        // eslint-disable-next-line react/no-unstable-nested-components
        cell: (item) => <LotStatusBadge status={item.status} />,
      },
      {
        id: 'madeFrom',
        header: 'Made from',
        // eslint-disable-next-line react/no-unstable-nested-components
        cell: (item) => (item.sourceLotIds?.length ? (
          <SpaceBetween direction="horizontal" size="xs">
            {item.sourceLotIds.map((sourceLotId) => (
              <LotDisplay key={sourceLotId} lotId={sourceLotId} link />
            ))}
          </SpaceBetween>
        ) : '—'),
      },
      {
        id: 'created',
        header: 'Created',
        // eslint-disable-next-line no-underscore-dangle
        cell: (item) => item._createdBy?.timestamp?.toDate?.().toLocaleString() || '—',
      },
    ],
    [navigate],
  );

  return (
    <Table
      header={<Header variant="h2">Bags</Header>}
      filter={(
        <SpaceBetween size="s">
          <SpaceBetween direction="horizontal" size="s">
            <SegmentedControl
              selectedId={kind}
              options={kindOptions}
              onChange={({ detail }) => onFiltersChange({ kind: detail.selectedId })}
            />
            <SegmentedControl
              selectedId={status}
              options={statusOptions}
              onChange={({ detail }) => onFiltersChange({ status: detail.selectedId })}
            />
          </SpaceBetween>
          <Grid
            gridDefinition={[
              { colspan: { default: 6, s: 3 } },
              { colspan: { default: 6, s: 3 } },
              { colspan: { default: 12, s: 4 } },
              { colspan: { default: 12, s: 2 } },
            ]}
          >
            <Select
              selectedOption={selectedProjectOption}
              options={projectFilterOptions}
              statusType={projectsLoading ? 'loading' : 'finished'}
              filteringType="auto"
              expandToViewport
              onChange={({ detail }) => onFiltersChange({
                projectDocId: detail.selectedOption.value,
                // Parts are project-scoped — a project change orphans it.
                pPartDocId: null,
              })}
            />
            <Select
              selectedOption={selectedPartOption}
              options={partFilterOptions}
              statusType={partsLoading && projectDocId ? 'loading' : 'finished'}
              filteringType="auto"
              expandToViewport
              empty="No parts found"
              disabled={!projectDocId}
              onChange={({ detail }) => onFiltersChange({
                pPartDocId: detail.selectedOption.value,
              })}
            />
            <TextFilter
              filteringPlaceholder="Code starts with… e.g. L-20260910"
              filteringText={filterText}
              onChange={({ detail }) => onFiltersChange(
                { code: detail.filteringText },
                { replace: true },
              )}
            />
            <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              {hasActiveFilters && (
                <Button variant="inline-link" onClick={onClearFilters}>
                  Clear filters
                </Button>
              )}
            </div>
          </Grid>
        </SpaceBetween>
      )}
      variant="full-page"
      stickyHeader
      resizableColumns
      wrapLines
      loading={isFetchingNextPage}
      items={pageData}
      columnDefinitions={columnDefinitions}
      pagination={<CustomPagination />}
      empty={<div>No lots</div>}
    />
  );
}

BagsTab.propTypes = {
  projectDocId: PropTypes.string,
  pPartDocId: PropTypes.string,
  kind: PropTypes.string,
  status: PropTypes.string,
  filterText: PropTypes.string,
  onFiltersChange: PropTypes.func.isRequired,
  onClearFilters: PropTypes.func.isRequired,
  projectFilterOptions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.string,
  })).isRequired,
  selectedProjectOption: PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.string,
  }).isRequired,
  projectsLoading: PropTypes.bool.isRequired,
};
